import { z } from "zod/v4";
export const importedInvoiceAnalysisSchema = z.object({
    version: z.literal(1),
    analyzedAt: z.date(),
    extractionMethod: z.enum(["batch", "to-pay"]),
    supplierName: z.string().nullable(),
    invoiceType: z.enum(["normal", "resto"]),
    invoiceReference: z.string().nullable(),
    amountCent: z.number().int(),
    invoiceDate: z.date().nullable(),
    debitDate: z.date().nullable(),
    paymentDueDate: z.date().nullable(),
    voucherAmountCent: z.number().int().nullable(),
    feesAmountCent: z.number().int().nullable(),
});
export const readImportedInvoiceAnalysis = (value) => {
    const parsed = importedInvoiceAnalysisSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
};
export const manualInvoiceUploadAnalyzeItemSchema = z.object({
    docId: z.string().nullable(),
    originalFileName: z.string(),
    pdfUrl: z.string().url().nullable(),
    emetteur: z.string().nullable(),
    invoiceType: z.enum(["normal", "resto"]).nullable(),
    modeRef: z.string().nullable(),
    montantCent: z.number().int().nullable(),
    dateFacture: z.string().datetime().nullable(),
    paymentDueDate: z.string().datetime().nullable(),
    montantTitresCent: z.number().int().nullable(),
    montantFraisCent: z.number().int().nullable(),
    error: z.string().nullable(),
});
export const manualInvoiceUploadAnalyzeResponseSchema = z.object({
    items: z.array(manualInvoiceUploadAnalyzeItemSchema),
});
//une facture peut avoir un compte et une date
//une facture peut avoir un compte et des règlements, dans ce cas-là il n'y a pas de date
//une facture peut avoir des ventilations globales et des règlements, dans ce cas-là il n'y a pas de date ni de compte
//une facture peut avoir des ventilations, dans ce cas-là il n'y a pas de compte
//une facture peut avoir des règlements et des ventilations par règlement, dans ce cas-là il n'y a pas de date ni de compte
//on résume: on ne peut pas avoir un compte et des ventilations en même temps
//on ne peut pas avoir des règlements et une date
//mais l'inverse est vrai aussi si on n'a pas de compte, il faut des ventilations ou des règlements avec ventilations
//si on n'a pas de date, il faut des règlements
//c'est la seule chose qui importe !
export const ventilationSchema = z.object({
    compte: z.string().min(1),
    montantCent: z.number().int(), //peut être négatif?
    pourcentageCent: z.number().int().gte(0).lte(10000).nullable().default(null),
});
export const règlement3InputSchema = z.strictObject({
    date: z.date(),
    montantCent: z.number().int(), //peut être négatif
    banqueId: z.string().nullable().default(null),
    ventilations: z.array(ventilationSchema).default([]),
});
export const règlement3Schema = règlement3InputSchema.extend({
    locked: z.boolean().default(false),
});
const règlementWithVentilations = z.object({
    date: z.date(),
    montantCent: z.number().int(), //peut être négatif
    banqueId: z.string().nullable().default(null),
    ventilations: z.array(ventilationSchema).default([]),
});
const newFactureWithCompteSchema = z.object({
    libellé: z.string().min(1),
    montantCent: z.number().int(),
    mode: z.enum([
        "Espèces",
        "Carte1",
        "CartePerso",
        "Virement1",
        "Chèque1",
        "Prélèvement1",
    ]),
    modeRef: z.string(),
    chequeNumber: z.string().trim().nullable().optional(),
    docId: z.string().nullable().default(null),
    compte: z.string().min(1),
    ventilations: z.array(ventilationSchema).optional(),
    règlements: z.array(règlement3InputSchema).optional(),
    date: z.date().optional(),
    banqueId: z.string().optional(),
    validationDate: z.date().optional(),
});
const newFactureWithVentilationsSchema = z.object({
    libellé: z.string().min(1),
    montantCent: z.number().int(),
    mode: z.enum([
        "Espèces",
        "Carte1",
        "CartePerso",
        "Virement1",
        "Chèque1",
        "Prélèvement1",
    ]),
    modeRef: z.string(),
    chequeNumber: z.string().trim().nullable().optional(),
    docId: z.string().nullable().default(null),
    ventilations: z.array(ventilationSchema),
    règlements: z.array(règlement3InputSchema).optional(),
    date: z.date().optional(),
    banqueId: z.string().optional(),
    validationDate: z.date().optional(),
});
const newFactureWithRèglementsWithVentilationsSchema = z.object({
    libellé: z.string().min(1),
    montantCent: z.number().int(),
    mode: z.enum([
        "Espèces",
        "Carte1",
        "CartePerso",
        "Virement1",
        "Chèque1",
        "Prélèvement1",
    ]),
    modeRef: z.string(),
    chequeNumber: z.string().trim().nullable().optional(),
    docId: z.string().nullable().default(null),
    règlementSchema: z.array(règlementWithVentilations),
    banqueId: z.string().optional(),
    validationDate: z.date().optional(),
});
export const newFactureSchema = z.union([
    newFactureWithCompteSchema,
    newFactureWithVentilationsSchema,
    newFactureWithRèglementsWithVentilationsSchema,
]);
const existingFactureWithCompteSchema = newFactureWithCompteSchema.extend({
    _id: z.string().min(1),
    owner: z.string().min(1),
});
const existingFactureWithVentilationsSchema = newFactureWithVentilationsSchema.extend({
    _id: z.string().min(1),
    owner: z.string().min(1),
});
export const existingFacture2Schema = z.union([
    existingFactureWithCompteSchema,
    existingFactureWithVentilationsSchema,
]);
const facture3InputBaseSchema = z.strictObject({
    libellé: z.string().min(1),
    montantCent: z.number().int(),
    mode: z.enum([
        "Espèces",
        "Carte1",
        "CartePerso",
        "Virement1",
        "Chèque1",
        "Prélèvement1",
    ]),
    modeRef: z.string(),
    chequeNumber: z.string().trim().nullable().optional(),
    docId: z.string().nullable().default(null),
    compte: z.string().nullable().default(null),
    ventilations: z.array(ventilationSchema),
    règlements: z.array(règlement3InputSchema),
    date: z.date().nullable(),
    datePrelevement: z.date().nullable().optional(),
    banqueId: z.string().nullable().default(null),
    validationDate: z.date().nullable().default(null),
});
export const newFacture3InputSchema = facture3InputBaseSchema.superRefine(facture3SuperRefine);
export const newFacture3Schema = newFacture3InputSchema;
const editFacture3InputBaseSchema = facture3InputBaseSchema.extend({
    _id: z.string().min(1),
    owner: z.string().min(1),
});
export const editFacture3InputSchema = editFacture3InputBaseSchema.superRefine(facture3SuperRefine);
const existingFacture3BaseSchema = facture3InputBaseSchema.extend({
    _id: z.string().min(1),
    owner: z.string().min(1),
    règlements: z.array(règlement3Schema),
    locked: z.boolean().default(false),
});
export const existingFacture3Schema = existingFacture3BaseSchema.superRefine(facture3SuperRefine);
function facture3SuperRefine(obj, ctx) {
    if (!obj.compte &&
        obj.ventilations.length === 0 &&
        (obj.règlements.length === 0 ||
            obj.règlements.some((r) => r.ventilations.length === 0))) {
        ctx.addIssue({
            message: "Il faut soit une ventilation générale, soit des ventilations par règlement soit un compte",
            code: "custom",
        });
    }
    if (!obj.date) {
        if (obj.règlements.length === 0) {
            ctx.addIssue({
                message: "Il faut soit une date pour la facture, soit des règlements multiples",
                code: "custom",
            });
        }
    }
    if (obj.compte && obj.ventilations.length > 0) {
        ctx.addIssue({
            message: "On ne peut pas avoir un compte et des ventilations",
            code: "custom",
        });
    }
    if (obj.compte && obj.règlements.some((r) => r.ventilations.length > 0)) {
        ctx.addIssue({
            message: "On ne peut pas avoir un compte et des ventilations par règlement",
            code: "custom",
        });
    }
    if (obj.date && obj.règlements.length > 0) {
        ctx.addIssue({
            message: "On ne peut pas avoir des règlements et une date",
            code: "custom",
        });
    }
    if (obj.mode === "Chèque1") {
        if (!obj.chequeNumber || !obj.chequeNumber.trim()) {
            ctx.addIssue({
                message: "Le numéro de chèque est obligatoire pour une facture chèque",
                code: "custom",
                path: ["chequeNumber"],
            });
        }
    }
}
//# sourceMappingURL=schemas.js.map